import { useState, useEffect, useMemo, useCallback } from 'react';

const STORAGE_KEY = 'twm_product_groups_filter_state';

// Mirrors Booqable's product_type values: 'rental' | 'sales_item' | 'service'.
// ('sales_item' was historically called 'consumable' in v1 APIs.)
export type ProductType = 'rental' | 'service' | 'sales_item';

export interface ProductGroupFilterState {
  productTypes: ProductType[];
  /** Filter sidebar open/closed — owned here so it survives navigation. */
  sidebarCollapsed: boolean;
}

const EMPTY_STATE: ProductGroupFilterState = {
  productTypes: [],
  sidebarCollapsed: false,
};

function loadState(): ProductGroupFilterState {
  if (typeof window === 'undefined') return EMPTY_STATE;
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const base = raw ? { ...EMPTY_STATE, ...JSON.parse(raw) } : EMPTY_STATE;
    return isMobile ? { ...base, sidebarCollapsed: true } : base;
  } catch {
    return isMobile ? { ...EMPTY_STATE, sidebarCollapsed: true } : EMPTY_STATE;
  }
}

function saveState(state: ProductGroupFilterState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota / private mode — silently fail; filters reset to defaults next visit.
  }
}

interface FilterableGroup {
  productType: ProductType | string;
}

export interface UseProductGroupFiltersResult {
  state: ProductGroupFilterState;
  setField: <K extends keyof ProductGroupFilterState>(key: K, value: ProductGroupFilterState[K]) => void;
  clearAll: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  applyFilters: <T extends FilterableGroup>(groups: T[]) => T[];
}

export function useProductGroupFilters(): UseProductGroupFiltersResult {
  const [state, setState] = useState<ProductGroupFilterState>(loadState);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const setField = useCallback(
    <K extends keyof ProductGroupFilterState>(key: K, value: ProductGroupFilterState[K]) => {
      setState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const clearAll = useCallback(() => {
    setState((prev) => ({ ...EMPTY_STATE, sidebarCollapsed: prev.sidebarCollapsed }));
  }, []);

  const activeFilterCount = useMemo(
    () => (state.productTypes.length > 0 ? 1 : 0),
    [state],
  );

  const hasActiveFilters = activeFilterCount > 0;

  const applyFilters = useCallback<UseProductGroupFiltersResult['applyFilters']>(
    (groups) => {
      if (state.productTypes.length === 0) return groups;
      const allowed = new Set<string>(state.productTypes);
      return groups.filter((g) => allowed.has(g.productType));
    },
    [state],
  );

  return { state, setField, clearAll, hasActiveFilters, activeFilterCount, applyFilters };
}
